import { useEffect, useMemo, useState } from "react";
import { useT, type Uebersetzer } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { useRuf } from "../schale/Ruf";
import { FehlerBand, Laedt } from "../bausteine";
import { Blattvonunten } from "../bausteine/Blattvonunten";
import { Trefferzeile, wiederFreiSatz } from "../bausteine/Trefferzeile";
import { Trefferauskunft } from "../bausteine/Trefferauskunft";
import { ausstattungVon, mitAusstattung } from "../bausteine/Ausstattung";
import {
  Abschnitt, Leersatz, Suchfeld, Wertzeile, Zeilenblatt,
} from "../bausteine/Handyformen";
import { Zeichen } from "../bausteine/Zeichen";
import { useKunden, useMaschinentypen, usePreis } from "../daten/haken";
import {
  heuteTag, tagPlus, useNachschlagen, VORGABE_TAGE, type Typenlage,
} from "../daten/nachschlagen";
import { alsBuendelpreis, usePreisbuendel, type Buendelpreis } from "../daten/preisbuendel";
import { alsAnhaenger } from "../daten/abholung";
import { useMerkliste, warSchonGemerkt, type Merkwunsch } from "../daten/merkliste";
import { ladestandVon } from "../daten/ladestand";
import type { Kunde, Maschinentyp } from "../daten/typen";
import { bisTag, Zeitraumwahl } from "./handy/Zeitraumblatt";
/* Die Gerätegruppen — seit dem 19.09.2026 ein Baustein, weil die
   Vermietfläche am Telefon dieselbe Reihe braucht. */
import { Suchfilter } from "../bausteine/Suchfilter";
import { ohnePreisrecht } from "./handy/rechte";
import "../stile/handy.css";
import "../stile/telefon/nachschlagen.css";
import { vormerkTeile } from "../daten/reserviertsatz";

/* ══════════════════════════════════════════════════════════════════
   Nachschlagen am Telefon — dieselbe Liste wie beim Vermieten.

   Luca, 17.09.2026: *„Wir wollen die Mobile-App ja nicht an die Adresse
   binden, sondern daran, dass wir von dem Mobile aus arbeiten."*

   **Bis hierher gab es die Auskunft dreimal:** das Blatt der Anwendung
   (`masken/Nachschlagen.tsx`), `/handy/nachschlagen` („ist das Gerät
   frei?") und `/handy/preis` („was kostet das?"). Jetzt ist es das eine
   Blatt, das am Telefon von unten hereinkommt und sich wegziehen lässt;
   beide alten Adressen öffnen es und leiten um (`router.tsx`).

   ── Der Durchgang vom 19.09.2026 ──────────────────────────────────

   Luca: *„Die Nachschlagemasken hast du auch noch nicht fertig gemacht.
   Da bitte auch noch mal wieder bei."* Und für alle Telefonschirme:
   *„…das muss einheitlich sein von der Struktur, sodass die Buttons
   sauber sind und diese Einträge in der Liste immer gleich hoch sind."*

   **Der Schirm sprach seine eigene Sprache.** Er hatte ein graues
   Suchfeld ohne Lupe, wo jeder andere Schirm den Rahmen mit der Lupe
   trägt; eine eigene Trefferzeile (`hn-treffer`), die dieselbe Frage
   beantwortete wie die des Vermietens, nur anders; einen gelben
   „Merken"-Knopf in jeder aufgeklappten Zeile, wo das Vermieten den
   ruhigen Griff an der Zeile hat; und eine Auskunft in zwei Stufen
   („kurzer Blick", „Rechenweg, Staffel und Stücke"), die aufgeklappt
   rund 660 px hoch war und die Liste wegschob.

   | was jetzt | woher |
   |---|---|
   | **das Suchfeld** | `Suchfeld` (`bausteine/Handyformen.tsx`) — wie Suche, Vermieten, Ressourcen, Preisstamm |
   | **die Gerätegruppen** | im Blatt hinter dem Filterknopf (`Suchfilter`), wie beim Vermieten |
   | **der Zeitraum** | eine ruhige Zeile im `Zeilenblatt`, 48 px |
   | **die Treffer** | `Trefferzeile` mit „+ Merken" — Zeile für Zeile dieselbe wie beim Vermieten |
   | **die Auskunft** | ein eigenes **Blatt**, nicht mehr ein Aufklapper in der Liste |

   **Warum ein Blatt und kein Aufklapper.** Luca, 18.09.2026: *„dann
   klicke ich einen an — und dann ist das halt riesig, ne?"* Die Antwort
   darauf war bis heute, die Auskunft in zwei Stufen zu teilen; damit war
   sie kurz, aber die Liste sprang trotzdem, und die zweite Stufe stand
   erst recht mitten darin. Als Blatt bleibt die Liste, wo sie ist: Das
   Blatt legt sich darüber, rollt für sich, und wer es wegzieht, steht
   wieder genau bei seiner Zeile. **Merken geht ohne das Blatt** — der
   Griff steht an der Zeile.

   ── Was das Blatt der Auskunft trägt ──────────────────────────────

     · **Gerechnet mit** — Ausstattung, Bediener, für wen der Preis gilt.
       Drei Annahmen, die den Betrag ändern; sie stehen oben, weil man
       sie hier ändern kann.
     · **Die Auskunft selbst** (`bausteine/Trefferauskunft.tsx`) — Preis
       mit Rechenweg, Zuschläge, Staffel, Stücke. Dieselbe wie am
       Schreibtisch und beim Vermieten, am Telefon in Wertzeilen.
     · **Im Fuß die eine gelbe Handlung: „Merken".** Daneben ruhig „Als
       Angebot" (Regel 10: der Regelfall hat kein Angebot).

   ── Für wen der Preis gilt ────────────────────────────────────────

   Luca, 18.09.2026: *„Da habe ich auch irgendwie so einen Testkunden
   vorausgewählt, obwohl ich noch gar keinen gewählt hatte."* Das Blatt
   liest den Empfänger der Sammlung deshalb **nicht** als seinen Kunden —
   der steht dort über Sitzungen hinweg, und wer heute nachschlägt, bekäme
   die Kondition von vorletzter Woche, ohne dass es dasteht. **Ohne
   gewählten Kunden gilt der Listenpreis, und beides steht im Fuß der
   Liste und im Blatt.** Der Weg hinaus bleibt: Wer hier einen Kunden
   wählt, hat für den Entwurf gewählt — er geht in die Sammlung, und der
   Abschluss fragt nicht noch einmal (Regel 11).

   **Gerechnet wird nicht hier.** Die Lage kommt aus `GET /nachschlagen`,
   die Preise der ganzen Liste aus `GET /preise`. `GET /preis` läuft nur
   für den einen Typ, dessen Annahme jemand im Blatt geändert hat — es ist
   dieselbe Stelle, aus der eine Position im Auftrag ihren Preis bekommt.

   Klassen: `hn-*` und `tat-*` aus `stile/telefon/nachschlagen.css`, die
   Trefferzeile aus `stile/telefon/vermieten.css`, die Formen aus
   `handy.css`. Gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/* ── Die Vorbelegung ─────────────────────────────────────────────────
   Die Adresse trägt sie mit: `/preis?typ=…&von=…&tage=…` (bis zum
   19.09.2026 `/handy/preis?…`, das weiterhin dorthin führt).
   Das Blatt ist kein Weg und hat keine Suchwerte — also legt die Route sie
   hier ab, bevor sie das Blatt öffnet, und das Blatt nimmt sie beim
   Aufgehen **einmal** (danach gehört der Stand dem, der tippt). */
export type Nachschlagvorgabe = { typ?: string; von?: string; tage?: number };
let vorgabe: Nachschlagvorgabe | null = null;

export function nachschlagenVorbelegen(neu: Nachschlagvorgabe): void {
  vorgabe = neu;
}

/**
 * Der gewählte Kunde, so viel wie dieses Blatt von ihm braucht.
 *
 * Nummer und Name — mehr steht auch in der Sammlung nicht (`Merkempfaenger`),
 * und der volle Satz aus dem Stamm wäre eine zweite Abfrage für zwei Wörter.
 */
type Gewaehlt = { nr: string; name: string };

/** Der gefragte Zeitraum: ab wann, wie viele Kalendertage. */
type Zeit = { von: string; tage: number };

/**
 * Was jemand an einem Typ von Hand anders angenommen hat.
 *
 * `null` heißt **unangetastet** — dann gilt, was die Liste gerechnet hat.
 * Erst wer die Ausstattung wechselt oder den Bediener dazunimmt, stellt eine
 * andere Frage; dann und nur dann geht ein `GET /preis` für diesen einen Typ
 * hinaus. Die Annahmen stehen in der Maske und nicht in der Zeile, damit die
 * Zeile und das Blatt darüber **dieselbe** Zahl zeigen (28-designkonzept.md,
 * „Zwei Zahlen, beide richtig").
 */
type Annahme = { variante: string | null; bedient: boolean | null };
const UNANGETASTET: Annahme = { variante: null, bedient: null };

/**
 * Die eine Zeile, die „was ist frei, ab wann" beantwortet.
 *
 * **Die Zahl zuerst, nicht das Wort** — „3 von 5 frei" sagt mehr als „Frei",
 * und wo nichts frei ist, sagt der zweite Satz, ab wann wieder. Beide kommen
 * aus `bausteine/Trefferzeile.tsx`, also aus derselben Vokabel wie am
 * Schreibtisch und im Brett.
 *
 * **Reserviert ist weder frei noch belegt** (18.09.2026): Was ein Angebot für
 * seine Bindefrist hält, zählt nicht mehr zu den freien Stücken. Ohne den
 * Zusatz stünde am Telefon „Keins frei" an einem Gerät, das auf dem Hof
 * steht — und der Außendienst sagte ab, statt zu fragen.
 */
function lageSatz(t: Uebersetzer, z: Typenlage, wo: string): string {
  if (z.bestand === 0) return t("lage.kein_geraet");
  const teile = [
    wiederFreiSatz(t, z) || t("nachschlagen.freiVon", { frei: z.frei, bestand: z.bestand }),
    ...vormerkTeile(z).map((teil) => t(teil.schluessel, { zahl: teil.zahl })),
    z.werkstatt > 0 ? t("nachschlagen.inWerkstatt", { zahl: z.werkstatt }) : "",
    wo,
  ];
  return teile.filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);
}

/** Der Weg zum Angebot — die Vorbelegung in der Adresse. */
function angebotsweg(
  felder: { typ: string; von: string; bis: string; variante: string; kunde: string },
): string {
  const teile = new URLSearchParams({ typ: felder.typ, von: felder.von, bis: felder.bis });
  if (felder.variante) teile.set("variante", felder.variante);
  if (felder.kunde) teile.set("kunde", felder.kunde);
  return `/angebote/neu?${teile.toString()}`;
}

/**
 * Welcher Preis für diesen Typ gerade gilt — der aus dem Bündel, oder der
 * eigene, wenn jemand eine Annahme geändert hat.
 *
 * **Vorbelegt wird aus dem Bündel und nicht aus dem Stamm.** Das ist der
 * Unterschied, an dem bis zum 18.09.2026 zwei Zahlen entstanden: Die Liste
 * rechnete den Grundpreis, die aufgeklappte Zeile die Standardausstattung —
 * beide richtig, beide verschieden, zehn Pixel auseinander.
 */
function useGilt(z: Typenlage, preis: Buendelpreis | undefined, annahme: Annahme, zeit: Zeit, kunde: Gewaehlt | null): {
  gilt: Buendelpreis | undefined;
  /** Die Zahl steht, gilt aber noch der vorigen Annahme. */
  frisch: boolean;
  variante: string;
  bedient: boolean;
  bis: string;
} {
  const bis = bisTag(zeit.von, zeit.tage);
  const angefasst = annahme.variante !== null || annahme.bedient !== null;
  const variante = annahme.variante ?? preis?.varianteId ?? "";
  const bedient = annahme.bedient ?? preis?.bedient ?? false;

  const einzeln = usePreis(
    angefasst ? z.typId : undefined,
    zeit.von, bis, kunde?.nr ?? null, bedient, variante || undefined,
    { halten: true },
  );
  const gilt = useMemo(
    () => (angefasst && einzeln.data
      ? alsBuendelpreis(z.typId, einzeln.data, bis, variante)
      : preis),
    [angefasst, einzeln.data, z.typId, bis, variante, preis],
  );
  const frisch = angefasst && (einzeln.isFetching || einzeln.isPlaceholderData === true);
  return { gilt, frisch, variante, bedient, bis };
}

/**
 * Was ein gemerkter Posten trägt — dasselbe wie aus der Vermietfläche.
 *
 * Gemerkt wird, was die Auskunft **beantwortet** hat: der Zeitraum aus der
 * Preisantwort (mit dem gerechneten Ende), die Miettage aus derselben, die
 * Zuschläge, die ohne Nachfrage mitlaufen, und was das Gerät an Bedienung
 * verlangt (Migration 0083). Ein Posten vom Telefon darf nicht ärmer in der
 * Sammlung ankommen als einer vom Schreibtisch.
 */
function merkwunsch(
  z: Typenlage, typ: Maschinentyp | undefined, gilt: Buendelpreis | undefined,
  zeit: Zeit, bis: string, variante: string, bedient: boolean,
): Merkwunsch {
  const vorgewaehlt = (gilt?.zuschlaege ?? []).filter((x) => x.vorgewaehlt);
  return {
    typId: z.typId,
    typ: z.typ,
    bezeichnung: mitAusstattung(z.typ, typ, variante),
    gruppe: z.uebergruppe || z.gruppe,
    ...(variante
      ? { varianteId: variante, variante: typ?.varianten.find((v) => v.id === variante)?.label ?? "" }
      : {}),
    von: zeit.von,
    bis: gilt?.bis || bis,
    ...(gilt && !gilt.ohnePreis
      ? {
        miettage: gilt.miettage,
        netto: gilt.netto,
        einheiten: gilt.menge,
        einheitKey: gilt.einheitKey,
        /* **Auf diese Pauschale schlaegt bewusst nichts auf** (0144) — wie
           am Schreibtisch. */
        zuschlagsfrei: gilt.zuschlagsfrei,
        zuschlaegeNetto: vorgewaehlt.reduce((s, x) => s + x.netto, 0),
        zuschlaege: vorgewaehlt.map((x) => ({
          code: x.code, bezeichnung: x.bezeichnung, netto: x.netto,
          menge: x.menge,
          // Kein `einzelpreis` — wie am Schreibtisch (V-13/V-15/V-24).
          ...(x.jeAuftrag ? { jeAuftrag: true } : {}),
          ...(x.platzhalter ? { platzhalter: true } : {}),
          ...(x.faelltBeiAbholung ? { faelltBeiAbholung: true } : {}),
          ...(x.preisIndividuell ? { preisIndividuell: true } : {}),
        })),
      }
      : {}),
    ...(gilt ? { bedienungspflicht: gilt.bedienungspflicht } : {}),
    /* Abholbar, und was dann mitgeht (Migration 0127) — am Telefon genauso
       wie am Schreibtisch. */
    ...(gilt?.abholung?.abholbar ? { abholbar: true } : {}),
    ...(gilt?.abholung?.anhaenger ? { anhaenger: alsAnhaenger(gilt.abholung.anhaenger) } : {}),
    bedient,
  };
}

/* ══════════════ Eine Zeile in der Liste ══════════════
   **Dieselbe Zeile wie beim Vermieten** (`bausteine/Trefferzeile.tsx`,
   Telefonmaße in `stile/telefon/vermieten.css`): oben Name und Betrag, unten
   Lage, Gruppe und Miettage, rechts der ruhige Griff „+ Merken". Sie öffnet
   das Blatt der Auskunft; gemerkt wird ohne das Blatt. */
function Treffer({ z, typ, preis, zeit, kunde, annahme, wo, offen, oeffnen }: {
  z: Typenlage;
  typ: Maschinentyp | undefined;
  /** Der Preis aus dem Bündel — eine Frage für die ganze Liste, nicht acht. */
  preis: Buendelpreis | undefined;
  zeit: Zeit;
  kunde: Gewaehlt | null;
  annahme: Annahme;
  /** Die engste Gerätegruppe — oder nichts, wenn sie die gewählte Pille wiederholt. */
  wo: string;
  offen: boolean;
  oeffnen: () => void;
}) {
  const t = useT();
  const ruf = useRuf();
  const { merkenWennFrei } = useMerkliste();
  const { gilt, frisch, variante, bedient, bis } = useGilt(z, preis, annahme, zeit, kunde);

  const merkenJetzt = async () => {
    const wunsch = merkwunsch(z, typ, gilt, zeit, bis, variante, bedient);
    // Nur, was frei ist — sonst das Fenster und kein Ruf (19.09.2026).
    const gemerkt = await merkenWennFrei(wunsch);
    if (gemerkt) ruf(warSchonGemerkt(gemerkt) ? "schonInSammlung" : "inSammlung", { was: wunsch.bezeichnung ?? z.typ });
  };

  return (
    <Trefferzeile
      name={z.typ}
      wo={wo}
      lage={z}
      preis={gilt}
      frisch={frisch}
      gewaehlt={offen}
      waehlen={oeffnen}
      rechts={(
        /* **Kontur statt Fläche** (19.09.2026) — wie beim Vermieten: Fünf
           gelbe Knöpfe untereinander wären eine gelbe Säule am rechten Rand,
           und Hausgelb gehört auf diesem Schirm dem Knopf der Sammlung. */
        <button type="button" className="vmt-merken" onClick={() => { void merkenJetzt(); }}>
          <Zeichen name="plus" />
          <span>{t("merkliste.merken")}</span>
        </button>
      )}
    />
  );
}

/* ══════════════ Das Blatt der Auskunft ══════════════
   Oben die Annahmen, darunter die Auskunft, im Fuß die eine gelbe Handlung.
   **Die Annahmen bleiben hell, während die Zahl nachkommt** — sie stehen
   schon richtig; leise wird nur, was noch der vorigen Annahme gilt. */
function Auskunftsblatt({
  z, typ, preis, zeit, kunde, annahme, setzeAnnahme, waehleKunde, schliessen, wo,
}: {
  z: Typenlage;
  typ: Maschinentyp | undefined;
  preis: Buendelpreis | undefined;
  zeit: Zeit;
  kunde: Gewaehlt | null;
  annahme: Annahme;
  setzeAnnahme: (neu: Annahme) => void;
  waehleKunde: () => void;
  schliessen: () => void;
  wo: string;
}) {
  const t = useT();
  const ruf = useRuf();
  const { merkenWennFrei } = useMerkliste();
  const { gilt, frisch, variante, bedient, bis } = useGilt(z, preis, annahme, zeit, kunde);
  const [ausstattungOffen, setzeAusstattungOffen] = useState(false);

  const art = ausstattungVon(typ);
  const variantenName = typ?.varianten.find((v) => v.id === variante)?.label ?? "";
  /* `ohne_person` ist keine Frage, sondern eine Eigenschaft des Geräts — ein
     Rüttler geht nie mit Fahrer hinaus, ein Kran nie ohne (0083/0096). */
  const fragtNachBediener = typ?.bedienung.pflicht === "wahlweise";
  const immerBediener = typ?.bedienung.pflicht === "nur_mit_person";

  const merkenJetzt = async () => {
    const wunsch = merkwunsch(z, typ, gilt, zeit, bis, variante, bedient);
    /* Geht es nicht, bleibt das Blatt offen: Darin stehen Ausstattung und
       Zeitraum, und genau dort lässt sich die Frage ändern. Das Fenster
       legt sich darüber. */
    if (!(await merkenWennFrei(wunsch))) return;
    ruf("inSammlung", { was: wunsch.bezeichnung ?? z.typ });
    schliessen();
  };

  return (
    <Blattvonunten
      offen
      schliessen={schliessen}
      titel={z.typ}
      unter={lageSatz(t, z, wo)}
      /* **`gross`, weil hier gelesen wird**: Preis, Rechenweg, Zuschläge,
         Staffel und Stücke sind zusammen länger als ein halber Schirm, und
         eine Staffel, von der man zwei Stufen sieht, lädt zum Verlesen ein
         (`Blattvonunten`, „Ein Blatt, in dem man arbeitet, wird groß"). */
      rast="gross"
      fuss={(
        <div className="hn-taten">
          {/* **Der Weg ins Angebot verlässt das Blatt** — deshalb steht er
              ruhig daneben und nicht in Hausgelb. Über die Sammlung geht
              derselbe Weg weiter (`MerklisteAbschluss`). */}
          <a
            className="handy-knopf leise"
            href={angebotsweg({
              typ: z.typId, von: zeit.von, bis, variante, kunde: kunde?.nr ?? "",
            })}
          >
            {t("handypreis.alsAngebot")}
          </a>
          <button type="button" className="handy-knopf" onClick={() => { void merkenJetzt(); }}>
            {t("merkliste.merken")}
          </button>
        </div>
      )}
    >
      <div className={`hn-blatt${frisch ? " wird-frisch" : ""}`}>
        {/* ── Womit gerechnet wird ────────────────────────────────────
            Drei Annahmen, die den Betrag ändern: die Ausstattung, der
            Bediener und der Kunde mit seiner Kondition. Sie stehen oben und
            nicht unten, weil die Zahl darunter von ihnen abhängt. */}
        <Abschnitt titel={t("nachschlagenTelefon.gerechnetMit")}>
          <Zeilenblatt>
            {art.gibt ? (
              <button type="button" className="hw-griff" onClick={() => setzeAusstattungOffen(true)}>
                <span className="hw-griff-schild">{t("positionen.ausstattung")}</span>
                <span className="hw-griff-wert">
                  {variantenName || t("positionen.ausstattungGrund")}
                </span>
                <span className="hw-griff-pfeil" aria-hidden="true" />
              </button>
            ) : null}

            {fragtNachBediener ? (
              <button
                type="button"
                className="hw-griff"
                aria-pressed={bedient}
                onClick={() => setzeAnnahme({ ...annahme, bedient: !bedient })}
              >
                <span className="hw-griff-schild">{t("handypreis.bediener")}</span>
                <span className="hw-griff-wert">
                  {t(bedient ? "handypreis.bedienerJa" : "handypreis.bedienerNein")}
                </span>
                <span className={`hw-schalter${bedient ? " an" : ""}`} aria-hidden="true" />
              </button>
            ) : immerBediener ? (
              <Wertzeile was={t("nachschlagenTelefon.bediener")}>
                {t("nachschlagenTelefon.bedienerImmer")}
              </Wertzeile>
            ) : null}

            {/* **Für wen die Zahl gilt.** Ohne gewählten Kunden ist es der
                Listenpreis, und das steht hier — nicht nur im Kommentar. Die
                Kondition entscheidet über den Preis; eine still gesetzte
                liefert eine Zahl, die für niemanden gilt. */}
            <button type="button" className="hw-griff" onClick={waehleKunde}>
              <span className="hw-griff-schild">{t("nachschlagenTelefon.preisFuer")}</span>
              <span className={`hw-griff-wert${kunde ? "" : " leise"}`}>
                {kunde ? kunde.name : t("nachschlagenTelefon.listenpreis")}
              </span>
              <span className="hw-griff-pfeil" aria-hidden="true" />
            </button>
          </Zeilenblatt>
        </Abschnitt>

        {/* **Ohne „Gerechnet mit …"** — die Ausstattung steht als Griff
            darüber, und zweimal derselbe Wert ist kein zweiter Hinweis. */}
        <Trefferauskunft lage={z} preis={gilt} typ={typ} ohneVariante />

        {/* B-17 (Durchgang 03.10.2026): Mit gewähltem Kunden enthält die Zahl
            seine Kondition — „Preis für: …" allein sagt das nicht. Der Satz
            war angelegt, stand aber nirgends. */}
        {kunde ? (
          <p className="ns-fuss">
            {t("nachschlagenTelefon.fussKondition", { kunde: kunde.name })}
          </p>
        ) : null}
      </div>

      {typ && art.gibt ? (
        <Blattvonunten
          offen={ausstattungOffen}
          schliessen={() => setzeAusstattungOffen(false)}
          titel={t("positionen.ausstattung")}
        >
          {art.grundpreis ? (
            <button
              type="button" className="hw-wahl" aria-pressed={variante === ""}
              onClick={() => { setzeAnnahme({ ...annahme, variante: "" }); setzeAusstattungOffen(false); }}
            >
              {t("positionen.ausstattungGrund")}
            </button>
          ) : null}
          {typ.varianten.map((v) => (
            <button
              key={v.id} type="button" className="hw-wahl" aria-pressed={variante === v.id}
              onClick={() => { setzeAnnahme({ ...annahme, variante: v.id }); setzeAusstattungOffen(false); }}
            >
              {v.label}
            </button>
          ))}
        </Blattvonunten>
      ) : null}
    </Blattvonunten>
  );
}

/**
 * Die Kundenwahl — freiwillig. Ohne Kunde gilt der Listenpreis, mit Kunde
 * seine Kondition (`dienste/preisfindung.ts`); keine ist die Vorstufe der
 * anderen.
 */
function Kundenblatt({ offen, schliessen, waehlen }: {
  offen: boolean;
  schliessen: () => void;
  waehlen: (k: Kunde | null) => void;
}) {
  const t = useT();
  const [wort, setzeWort] = useState("");
  const kunden = useKunden(wort.trim() || undefined);
  const zeilen = (kunden.data?.zeilen ?? []).slice(0, 25);

  return (
    <Blattvonunten
      offen={offen}
      schliessen={schliessen}
      titel={t("handypreis.kunde")}
      unter={t("handypreis.kundeWozu")}
    >
      <Suchfeld
        wert={wort}
        aendern={setzeWort}
        platz={t("handypreis.kundeSuchePlatz")}
        schild={t("handypreis.kundeSuche")}
      />
      <button type="button" className="hw-wahl" onClick={() => waehlen(null)}>
        {t("handypreis.ohneKunde")}
      </button>
      {kunden.isPending ? (
        <Laedt zeilen={4} />
      ) : zeilen.length ? (
        zeilen.map((k) => (
          <button key={k.nr} type="button" className="hw-wahl" onClick={() => waehlen(k)}>
            <span className="hw-wahl-name">{k.name}</span>
            <span className="hw-wahl-wo">
              {[k.nr, k.ort].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
            </span>
          </button>
        ))
      ) : (
        <Leersatz>{t("handypreis.keinKunde", { wort: wort.trim() })}</Leersatz>
      )}
    </Blattvonunten>
  );
}

/** Das Blatt am Telefon — steht im Blatt von unten, das man wegziehen kann. */
export function NachschlagenTelefon() {
  const t = useT();
  const { ich } = useSitzung();
  /* Gelesen im ersten Bild, geleert danach — nicht im Anfangswert selbst:
     Den ruft React im Entwicklungsmodus zweimal, und der zweite fände
     nichts mehr. */
  const [start] = useState<Nachschlagvorgabe>(() => vorgabe ?? {});
  useEffect(() => { vorgabe = null; }, []);
  const [wort, setzeWort] = useState("");
  const [gruppe, setzeGruppe] = useState<string | null>(null);
  const [zeit, setzeZeit] = useState<Zeit>({
    von: start.von ?? tagPlus(heuteTag(), 1),
    tage: start.tage ?? VORGABE_TAGE,
  });
  const [offen, setzeOffen] = useState<string | null>(start.typ ?? null);
  const [kundenblatt, setzeKundenblatt] = useState(false);
  /** Je Typ, was jemand von Hand anders angenommen hat. */
  const [annahmen, setzeAnnahmen] = useState<Record<string, Annahme>>({});
  const bis = bisTag(zeit.von, zeit.tage);

  /* ── Für wen der Preis gilt — und warum er hier bei null anfängt ──
     Der Grund steht oben im Kopf der Datei: Der Empfänger der Sammlung hält
     zwölf Stunden und über Sitzungen hinweg; als stiller Vorgewählter liefert
     er eine Zahl, die für niemanden gilt. **Der Weg hinaus bleibt offen** —
     wer hier wählt, wählt für den Entwurf. */
  const { setzeWer } = useMerkliste();
  const [kunde, setzeKunde] = useState<Gewaehlt | null>(null);

  const { data: typenStamm } = useMaschinentypen();
  const stammJe = useMemo(
    () => new Map((typenStamm?.zeilen ?? []).map((x) => [x.id, x])),
    [typenStamm],
  );

  /* Kommt die Adresse mit einem Typ (`/preis?typ=`), steht er im
     Suchfeld, sobald sein Name bekannt ist — und sein Blatt ist offen. */
  useEffect(() => {
    if (!start.typ || wort) return;
    const name = stammJe.get(start.typ)?.label;
    if (name) setzeWort(name);
  }, [start.typ, stammJe, wort]);

  const antwort = useNachschlagen({ wort, gruppe, von: zeit.von, bis });
  const daten = antwort.data;
  const wirdFrisch = antwort.isPlaceholderData;
  const gefragt = wort.trim().length > 0 || gruppe !== null;
  const zeilen = useMemo(() => daten?.zeilen ?? [], [daten]);

  /* **Was kostet es — für die ganze Liste in einer Frage.** Dieselbe Route
     und dieselbe Rechnung wie am Schreibtisch (`masken/Nachschlagen.tsx`),
     **mit der Kondition**, sobald ein Kunde gewählt ist. */
  const typen = useMemo(() => zeilen.map((z) => z.typId), [zeilen]);
  const { data: preise } = usePreisbuendel(
    { typen, von: zeit.von, bis, kunde: kunde?.nr ?? null },
    typen.length > 0,
  );
  const preisJe = useMemo(
    () => new Map((preise?.zeilen ?? []).map((p) => [p.typId, p])),
    [preise],
  );

  /** Die Gruppe an der Zeile — die engste, und keine, wenn sie die gewählte Pille wiederholt. */
  const gewaehlteGruppe = (daten?.gruppen ?? []).find((g) => g.id === gruppe)?.name ?? "";
  const engsteGruppe = (z: Typenlage): string => {
    const eng = z.gruppe || z.uebergruppe;
    return eng === gewaehlteGruppe ? "" : eng;
  };

  const offeneZeile = zeilen.find((z) => z.typId === offen) ?? null;

  if (ohnePreisrecht(ich?.rollen)) {
    return <Leersatz>{t("nachschlagen.nichtFuerDich")}</Leersatz>;
  }

  return (
    <div className="handy hn">
      {/* **Suchfeld und Filter in einer Zeile** (19.09.2026, abends) —
          derselbe Baustein wie beim Vermieten (`bausteine/Suchfilter.tsx`).
          Luca: *„Da musst du diese Suchobjekte dann in ein Share Component
          machen und das da genauso sauber machen, sodass es dann ein zweites
          Bottom Sheet hochgeht."* Die Gerätegruppen stehen im zweiten Blatt
          hinter dem Knopf; bis dahin standen sie hier als eigene Zeile
          („Alle Gerätegruppen", aufgeklappt drei Reihen Pillen). Darunter
          der Zeitraum, weil er eine Vorbelegung hat. */}
      <Suchfilter
        wert={wort}
        aendern={(neu) => { setzeWort(neu); setzeOffen(null); }}
        /* Neben dem Filterknopf ist das Feld 56 px schmaler — „…oder
           Inventarnu" stand angeschnitten da. */
        platz={t("nachschlagenTelefon.suchePlatz")}
        schild={t("nachschlagen.suchen")}
        wahl={daten?.gruppen ?? []}
        gewaehlt={gruppe}
        waehlen={(id) => { setzeGruppe(id); setzeOffen(null); }}
        filterTitel={t("suchfilter.geraetegruppe")}
        alleWort={t("nachschlagen.alleGruppen")}
      />

      {/* Der Zeitraum als **eine ruhige Zeile** — dieselbe Form wie der
          Rahmen beim Vermieten, und dasselbe Blatt dahinter
          (`masken/handy/Zeitraumblatt.tsx`). */}
      <Zeilenblatt>
        <Zeitraumwahl von={zeit.von} tage={zeit.tage} aendern={setzeZeit} />
      </Zeilenblatt>

      {/* **Die Antwort hat einen Namen** — „Treffer" links, die Zahl rechts,
          wie beim Vermieten und wie jeder Block der Handyfassung. */}
      <Abschnitt
        titel={t("nachschlagenTelefon.treffer")}
        {...(gefragt && !antwort.isPending && zeilen.length
          ? {
            auskunft: zeilen.length === 1
              ? t("nachschlagenTelefon.einTyp")
              : t("nachschlagenTelefon.typenZahl", { zahl: zeilen.length }),
          }
          : {})}
      >
        {antwort.isError ? (
          <FehlerBand pfad="fehler.unbekannt" nochmal={() => void antwort.refetch()} />
        ) : !gefragt ? (
          <Leersatz>{t("nachschlagenTelefon.anfang")}</Leersatz>
        ) : antwort.isPending || (zeilen.length === 0 && !ladestandVon(antwort).hatDaten) ? (
          /* Ohne Antwort auf DIESE Frage kein „Nichts gefunden" (N12-01) —
             auch nicht, solange die Treffer des vorigen Wortes gehalten werden. */
          <Laedt zeilen={3} />
        ) : zeilen.length === 0 ? (
          <Leersatz>
            {wort.trim()
              ? t("nachschlagen.nichts", { wort: wort.trim() })
              : t("nachschlagen.nichtsGruppe")}
          </Leersatz>
        ) : (
          <div className={`tab-rahmen hn-liste${wirdFrisch ? " wird-frisch" : ""}`} aria-busy={wirdFrisch}>
            <div className="tz-liste">
              {zeilen.map((z) => (
                <Treffer
                  key={z.typId}
                  z={z}
                  typ={stammJe.get(z.typId)}
                  preis={preisJe.get(z.typId)}
                  zeit={zeit}
                  kunde={kunde}
                  annahme={annahmen[z.typId] ?? UNANGETASTET}
                  wo={engsteGruppe(z)}
                  offen={offen === z.typId}
                  oeffnen={() => setzeOffen(z.typId)}
                />
              ))}
            </div>
          </div>
        )}
      </Abschnitt>

      {/* **Die zwei Fußnoten sind am 20.09.2026 weggefallen.** Luca: *„Wenn
          ich nachschlage, muss unten dieser Riesen-Text weg. … Das ist
          unnötig viel Text."* Sie sagten, dass die Lage der Planung folgt und
          die Beträge Listenpreise sind — beides steht schon oben an der
          Zeile, in der der Kunde gewählt wird („Listenpreis — kein Kunde
          gewählt" oder sein Name). Am Schreibtisch bleiben sie
          (`masken/Nachschlagen.tsx`): dort ist Platz, und die Zeile darüber
          gibt es nicht. */}

      {offeneZeile ? (
        <Auskunftsblatt
          key={offeneZeile.typId}
          z={offeneZeile}
          typ={stammJe.get(offeneZeile.typId)}
          preis={preisJe.get(offeneZeile.typId)}
          zeit={zeit}
          kunde={kunde}
          wo={[offeneZeile.uebergruppe, offeneZeile.gruppe].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
          annahme={annahmen[offeneZeile.typId] ?? UNANGETASTET}
          setzeAnnahme={(neu) => setzeAnnahmen((alt) => ({ ...alt, [offeneZeile.typId]: neu }))}
          waehleKunde={() => setzeKundenblatt(true)}
          schliessen={() => setzeOffen(null)}
        />
      ) : null}

      {/* Gewählt wird hier **und** in die Sammlung hinein — genau wie in der
          Vermietfläche (`Vermieten.tsx`). „Listenpreis" nimmt den Kunden
          wieder heraus; der Einsatzort geht dabei mit, weil die Baustelle
          des einen nicht dem nächsten gehört (`daten/merkliste.ts`). */}
      <Kundenblatt
        offen={kundenblatt}
        schliessen={() => setzeKundenblatt(false)}
        waehlen={(k) => {
          setzeKunde(k ? { nr: k.nr, name: k.name || k.nr } : null);
          setzeWer({
            nr: k?.nr ?? "",
            name: k?.name ?? "",
            art: k?.art === "interessent" || k?.art === "vermieter" ? k.art : "kunde",
          });
          setzeKundenblatt(false);
        }}
      />
    </div>
  );
}
